/**
 * The wire protocol.
 *
 * ONE RULE, and it is the reason this project can hold money:
 *
 *   CLIENTS SEND INPUT. SERVERS SEND TRUTH.
 *
 * There is no message in this file that lets a client assert a position, a
 * death, a pickup, a hit, or a win. v1 had six of those (`move`, `died`,
 * `collect`, `trigger_activated`, `respawn`, `platform_step`) and each one was
 * a way to take money out of other players' pockets with ten lines of
 * JavaScript.
 *
 * If you are adding a client->server message and it contains a noun rather than
 * a button, stop.
 */
// ---------------------------------------------------------------------------
// Timing
// ---------------------------------------------------------------------------
/** Server simulation rate. Fixed timestep, integer ticks, no wall clock inside a step. */
export const TICK_HZ = 30;
export const TICK_MS = 1000 / TICK_HZ;
/** How often the server ships a snapshot. */
export const SNAPSHOT_HZ = 15;
/**
 * How far behind the newest snapshot the client renders remote entities.
 * Buys one dropped packet plus jitter without visible rubber-banding.
 */
export const INTERPOLATION_DELAY_MS = 100;
/**
 * The universal dash.
 *
 * These live here rather than in the sim because three surfaces quote them and
 * only one of those can depend on `@chaos/sim`: the sim resolves the dash, the
 * game's HUD draws its cooldown, and the shell's character select explains it.
 * The shell cannot pull the whole simulation in to read two numbers, so before
 * this they were retyped there under a comment conceding they might drift.
 *
 * `TICK_HZ` above is the precedent — a number the sim needs but does not own.
 */
export const DASH_COOLDOWN_MS = 6000;
export const DASH_DURATION_MS = 150;
/**
 * What the player presses, in HUD order.
 *
 * `dash` is a control every character has and no character owns — it has no
 * `AbilityDef`, which is why a kit built from `CHARACTERS[id].abilities` alone
 * comes out one short of what the HUD actually binds. `abilityArt.ts` already
 * makes this point for the medallions; this is the same list for the bindings,
 * so the HUD, the help overlay and the character select cannot disagree about
 * which key does what.
 */
export const CONTROL_SLOTS = [
    { slot: "dash", key: "F / RMB", label: "Dash" },
    { slot: "basic", key: "LMB / C", label: "Basic" },
    { slot: "skill1", key: "Q", label: "Skill 1" },
    { slot: "skill2", key: "E", label: "Skill 2" },
    { slot: "ultimate", key: "R", label: "Ultimate" },
];
/** Inputs the server will accept per second per connection before it starts dropping. */
export const MAX_INPUTS_PER_SEC = TICK_HZ * 2;
/** How many ticks of history the server keeps for reconciliation and audit. */
export const SNAPSHOT_HISTORY_TICKS = TICK_HZ * 2;
// ---------------------------------------------------------------------------
// Client -> Server
// ---------------------------------------------------------------------------
/**
 * Bitfield. One bit per button. Never a position.
 *
 * NOT a `const enum`. A const enum lands in the emitted .d.ts as
 * `declare const enum Button`, and every workspace package compiles with
 * `verbatimModuleSyntax`, under which reading an AMBIENT const enum is TS2748.
 * That breaks the moment a consumer resolves this package through its built
 * dist/ instead of its source — i.e. every `turbo build`, while the
 * source-path typecheck stays green. A plain enum emits the same runtime object
 * and the same numbers, and `apps/game` reads `Button` as a value at runtime.
 */
export var Button;
(function (Button) {
    Button[Button["Jump"] = 1] = "Jump";
    Button[Button["Dash"] = 2] = "Dash";
    Button[Button["Basic"] = 4] = "Basic";
    Button[Button["Skill1"] = 8] = "Skill1";
    Button[Button["Skill2"] = 16] = "Skill2";
    Button[Button["Ultimate"] = 32] = "Ultimate";
})(Button || (Button = {}));
// ---------------------------------------------------------------------------
// Validation — the server's first line, before the sim sees anything
// ---------------------------------------------------------------------------
/**
 * Largest yaw change accepted in a single tick, radians.
 *
 * AT PI THIS IS INERT, and every clamp written against it is unreachable:
 * `shortestAngle` returns a value in [-PI, PI] by construction, so no pair of
 * angles can exceed PI and `Math.abs(delta) <= PI` is a tautology. The
 * consumer that clamps player facing is `sim/movement.ts#applyFacing`.
 *
 * That is deliberate, not an oversight — twin-stick aim is direct, and the
 * right stick setting facing instantly is the genre's expectation. It is
 * documented HERE, at the definition, because the claim is a property of this
 * value: an editor hover or a jump-to-definition has to show the truth, and
 * previously both showed "Rejects instant-snap aim scripts", which is false.
 *
 * Lower this and `applyFacing` becomes a real turn cap.
 * `apps/backend/src/room/inputGate.test.ts` pins the current value, so doing so
 * fails a test that tells you the cap has started to bite rather than silently
 * changing how every player aims.
 */
export const MAX_YAW_DELTA_PER_TICK = Math.PI;
export function sanitizeInputFrame(raw) {
    if (typeof raw !== "object" || raw === null)
        return null;
    const r = raw;
    const tick = r["tick"];
    const seq = r["seq"];
    const buttons = r["buttons"];
    const moveX = r["moveX"];
    const moveZ = r["moveZ"];
    const yaw = r["yaw"];
    if (!Number.isFinite(tick) || !Number.isFinite(seq))
        return null;
    if (!Number.isFinite(buttons))
        return null;
    if (!Number.isFinite(moveX) || !Number.isFinite(moveZ) || !Number.isFinite(yaw))
        return null;
    // Clamp rather than reject: a legitimate client with a rounding artifact
    // should keep playing, while a client claiming moveX=40 gets moveX=1.
    const mx = clamp(moveX, -1, 1);
    const mz = clamp(moveZ, -1, 1);
    const mag = Math.hypot(mx, mz);
    const nx = mag > 1 ? mx / mag : mx;
    const nz = mag > 1 ? mz / mag : mz;
    return {
        tick: Math.trunc(tick),
        seq: Math.trunc(seq),
        buttons: buttons & 0b111111,
        moveX: nx,
        moveZ: nz,
        yaw: normalizeAngle(yaw),
    };
}
function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
}
export function normalizeAngle(a) {
    const twoPi = Math.PI * 2;
    let x = a % twoPi;
    if (x > Math.PI)
        x -= twoPi;
    if (x < -Math.PI)
        x += twoPi;
    return x;
}
//# sourceMappingURL=wire.js.map